import { ASSETS, BRAND, buildWhatsAppLink } from "../lib/brand";
import { useReveal } from "../hooks/useReveal";
import { SectionHeader } from "../components/SectionHeader";
import { Sparkles, Heart, Award, Truck, MessageCircle } from "lucide-react";

const values = [
  { icon: Sparkles, title: "Fresh Quality Products", text: "We source the freshest harvests and roast in small batches for unmatched flavor." },
  { icon: Award,    title: "Premium Selection",      text: "Carefully chosen origins — Antep pistachios, Belgian chocolates, California almonds." },
  { icon: Heart,    title: "Ideal For Gifting",      text: "Elegant packaging designed to make every occasion feel a little more special." },
  { icon: Truck,    title: "Trusted Service",        text: "Open every day, 8:30 AM to midnight. Order on WhatsApp in under a minute." },
];

export default function About() {
  const ref = useReveal();
  return (
    <div ref={ref} data-testid="about-page" className="pt-20">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <img src={ASSETS.interiorStaff} alt="Inside Qamar Al Zahran" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-brand-ivory px-6">
          <span className="text-[11px] tracking-[0.32em] uppercase text-brand-gold animate-fade-in-up">Our Story</span>
          <h1 className="mt-4 font-serif-display text-4xl sm:text-6xl lg:text-7xl animate-fade-in-up" style={{animationDelay:"120ms"}}>
            Crafted with care.<br/><span className="text-brand-gold">Shared with love.</span>
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 md:py-28 bg-brand-ivory">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center reveal">
          <p className="font-serif-display text-2xl sm:text-3xl text-brand-brown leading-snug">
            Qamar Al Zahran is more than a roastery — it&apos;s a corner of the UAE where
            tradition, quality, and warmth meet on every shelf.
          </p>
          <p className="mt-6 text-brand-muted leading-relaxed">
            From the moment you walk into our store, you&apos;re greeted by the aroma of freshly roasted
            nuts, the glow of carefully arranged chocolates, and the colors of dried fruits from around
            the world. We believe premium food is best enjoyed when it&apos;s chosen with intention,
            prepared with skill, and shared with the people who matter.
          </p>
        </div>
      </section>

      {/* Split image + story */}
      <section className="py-16 md:py-24 bg-brand-beige/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="reveal rounded-3xl overflow-hidden shadow-2xl">
            <img src={ASSETS.storefront} alt="Storefront" className="zoom-img w-full h-full object-cover aspect-[4/5]" />
          </div>
          <div className="reveal">
            <span className="text-[11px] tracking-[0.28em] uppercase text-brand-gold font-semibold">The Roastery</span>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-brand-brown leading-tight">
              A neighborhood favorite, built on trust.
            </h2>
            <div className="mt-6 space-y-4 text-brand-muted leading-relaxed">
              <p>
                Tucked into the heart of the community, our roastery has grown one customer at a time —
                each return visit a reminder that quality and care still mean something.
              </p>
              <p>
                Every product on our shelves is tasted before it&apos;s offered. Every gift box is checked
                before it leaves our hands. It&apos;s the kind of attention that takes a moment longer,
                but it&apos;s the only way we know how to do this.
              </p>
              <p>
                Whether you&apos;re picking up a snack for the road, choosing favours for a wedding, or
                sending a corporate gift, we&apos;re honored to be part of the moment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Why Choose Us" title="The Qamar Al Zahran promise" testId="about-why-header" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div
                key={v.title}
                className="reveal p-7 rounded-2xl bg-brand-brown text-brand-ivory hover:-translate-y-1 transition-transform shadow-lg"
                style={{ transitionDelay: `${i*80}ms` }}
                data-testid={`about-value-${i}`}
              >
                <div className="h-11 w-11 rounded-full bg-brand-gold/20 text-brand-gold flex items-center justify-center mb-4">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="font-serif-display text-xl mb-2">{v.title}</h3>
                <p className="text-sm text-brand-ivory/75 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-beige/40">
        <div className="max-w-4xl mx-auto px-6 text-center reveal">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-brand-brown">Come say hello.</h2>
          <p className="mt-4 text-brand-muted">{BRAND.hours}</p>
          <a
            href={buildWhatsAppLink("Hello Qamar Al Zahran! I'd like to know more about your products.")}
            target="_blank" rel="noreferrer"
            data-testid="about-whatsapp-cta"
            className="mt-8 inline-flex items-center gap-2 bg-[#25D366] text-white px-7 py-3.5 rounded-full font-semibold hover:bg-[#1DA851] transition-all hover:shadow-xl"
          >
            <MessageCircle className="h-4 w-4" />
            Chat with us on WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
}
